import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, fetchWithRetry } from '../config';
import { useEscapeToClose } from '../hooks/useEscapeToClose';

/**
 * Message Center — notification bell + dropdown for user notifications.
 * Displays on the Dashboard header. Shows unread count badge.
 */
function MessageCenter({ fetchWithAuth }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // #80.1 — load on mount + refresh on tab focus/visibility instead of a blind 60s poll.
  // The idle poll landed on stale reused upstream connections during inactive gaps →
  // intermittent no-ACAO / ERR_FAILED console noise. Event-driven = no idle traffic.
  useEffect(() => {
    loadNotifications();
    const refreshOnActive = () => {
      if (document.visibilityState === 'visible') loadNotifications();
    };
    window.addEventListener('focus', refreshOnActive);
    document.addEventListener('visibilitychange', refreshOnActive);
    return () => {
      window.removeEventListener('focus', refreshOnActive);
      document.removeEventListener('visibilitychange', refreshOnActive);
    };
  }, []);

  // Refresh the moment the user opens the bell (fresh unread count on demand).
  useEffect(() => {
    if (isOpen) loadNotifications();
  }, [isOpen]);

  // Escape closes the panel — baseline keyboard expectation, same hook the modals use.
  useEscapeToClose(() => setIsOpen(false), isOpen);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await fetchWithRetry(() => fetchWithAuth(`${API_BASE}/api/notifications/?limit=10`));
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      // Silently fail — notifications are non-critical
    }
  };

  const markRead = async (id) => {
    try {
      await fetchWithAuth(`${API_BASE}/api/notifications/${id}/read`, { method: 'PUT' });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      // Silently fail
    }
  };

  const markAllRead = async () => {
    try {
      await fetchWithAuth(`${API_BASE}/api/notifications/read-all`, { method: 'PUT' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      // Silently fail
    }
  };

  const categoryIcon = (cat) => {
    switch (cat) {
      case 'warning': return '⚠️';
      case 'action': return '🔔';
      case 'security': return '🔒';
      default: return 'ℹ️';
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="mc-container" ref={dropdownRef}>
      <button className="mc-bell" onClick={() => setIsOpen(!isOpen)} title="Notifications">
        🔔
        {unreadCount > 0 && (
          <span className="mc-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        // The panel hangs from the top bar and therefore overlaps the page header beneath it —
        // unavoidable for any dropdown. What was NOT acceptable: a click landing on the panel's
        // inert chrome (the "No notifications" empty state, padding) did absolutely nothing, so a
        // user aiming at the "← Dashboard" button underneath got a dead click with no feedback.
        // Verified on Prod 2026-08-09: elementFromPoint at that button returned DIV.mc-empty and
        // a real click changed nothing. Now an inert-area click CLOSES the panel, so the gesture
        // has a visible result and the next click reaches the control they wanted.
        <div
          className="mc-dropdown"
          onClick={(e) => {
            if (e.target.closest('button, .mc-item')) return;  // real controls keep their behaviour
            setIsOpen(false);
          }}
        >
          <div className="mc-header">
            <span className="mc-headerTitle">Notifications</span>
            {unreadCount > 0 && (
              <button className="mc-markAll" onClick={markAllRead}>Mark all read</button>
            )}
          </div>
          <div className="mc-list">
            {notifications.length === 0 ? (
              <div className="mc-empty">No notifications</div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`mc-item ${n.is_read ? 'mc-item--read' : 'mc-item--unread'}`}
                  onClick={() => !n.is_read && markRead(n.id)}
                >
                  <span className="mc-itemIcon">{categoryIcon(n.category)}</span>
                  <div className="mc-itemContent">
                    <span className="mc-itemTitle">{n.title}</span>
                    <span className="mc-itemMessage">{n.message}</span>
                    <span className="mc-itemTime">{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageCenter;
